import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kExternalStorageBasePath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { MatterExternalSyncMappingResponse, MatterExternalSyncMappingResponseSchema } from '@/user_files/external-storage/schemas'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

export async function deleteMatterExternalSyncMapping(matterId: string): Promise<MatterExternalSyncMappingResponse> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kExternalStorageBasePath}/mappings/matter/${encodeURIComponent(matterId)}`
    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to disconnect matter external sync mapping: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping disconnect response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping disconnect response JSON')
    }

    const parsed = MatterExternalSyncMappingResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping disconnect response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping disconnect response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { matterId } })
      })
    }
    throw error
  }
}
